import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_METHODS = ["Email", "Phone", "Either"];

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  preferred_contact: "Email",
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Request received. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BUSINESS.phone);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-[#f2f4f0] placeholder:text-[#5c625b] focus:border-brand focus:outline-none transition-colors duration-200";

  return (
    <section
      id="contact"
      className="relative bg-obsidian py-24 sm:py-32 scroll-mt-20"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="text-xs uppercase tracking-[0.3em] text-brand font-semibold">
                Get In Touch
              </span>
              <span className="h-px w-16 bg-white/15" />
            </Reveal>
            <Reveal
              as="h2"
              delay={0.05}
              className="font-display font-bold uppercase tracking-tighter text-[#f2f4f0] text-4xl sm:text-6xl leading-[0.92]"
            >
              Request a Quote
              <br />
              <span className="text-[#8d948f]">or Site Walk.</span>
            </Reveal>

            <div className="mt-10 space-y-6">
              <a
                href={BUSINESS.mapLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-address"
              >
                <MapPin className="h-5 w-5 text-brand mt-0.5 shrink-0" />
                <span className="text-[#c7ccc4] group-hover:text-[#f2f4f0] transition-colors">
                  {BUSINESS.address}
                </span>
              </a>
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <Phone className="h-5 w-5 text-brand shrink-0" />
                <span className="text-[#c7ccc4] group-hover:text-[#f2f4f0] transition-colors">
                  {BUSINESS.phone}
                </span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-hours">
                <Clock className="h-5 w-5 text-brand shrink-0" />
                <span className="text-[#c7ccc4]">{BUSINESS.hours}</span>
              </div>
            </div>

            {/* Map */}
            <Reveal delay={0.1} className="mt-10 relative overflow-hidden border border-white/10">
              <iframe
                title="Delta Grassmasters location map"
                src={BUSINESS.mapEmbed}
                className="w-full h-[260px] grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="grid sm:grid-cols-2 gap-x-8 gap-y-7"
              data-testid="contact-form"
            >
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Name *
                </label>
                <input
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className={inputCls}
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Company
                </label>
                <input
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Company / organization"
                  className={inputCls}
                  data-testid="input-company"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Email *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                  className={inputCls}
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Phone *
                </label>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(501) 000-0000"
                  className={inputCls}
                  data-testid="input-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Preferred Contact Method
                </label>
                <select
                  value={form.preferred_contact}
                  onChange={update("preferred_contact")}
                  className={`${inputCls} appearance-none cursor-pointer`}
                  data-testid="input-preferred-contact"
                >
                  {CONTACT_METHODS.map((m) => (
                    <option key={m} value={m} className="bg-surface text-[#f2f4f0]">
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#8d948f]">
                  Message *
                </label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Tell us about your property and project..."
                  className={`${inputCls} resize-none`}
                  data-testid="input-message"
                />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 bg-brand px-8 py-4 font-bold text-[#0b120a] hover:bg-brand-hover transition-colors duration-200 text-sm uppercase tracking-wide disabled:opacity-60 group"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  )}
                  {loading ? "Sending..." : "Schedule a Site Walk"}
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
};
